import { makeObservable, observable, action } from 'mobx';
import { LayerWithSectionsDto, SubmissionScope } from '../../types';
import { SectionModel, NodeBaseModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';

export default class LayerWithSectionsModel {
    readonly id: string;

    readonly name: string;

    submissionScope: SubmissionScope | null;

    sections: SectionModel[] = [];

    expanded: boolean = false;

    constructor(
        private readonly node: NodeBaseModel,
        data: LayerWithSectionsDto,
        sources: SourceModelBase[]
    ) {
        this.id = data.id;
        this.name = data.name;
        this.submissionScope = data.submissionScope;
        this.sections = data.sections.map(section => new SectionModel(this.node, section, sources));

        makeObservable(this, {
            submissionScope: observable,
            sections: observable,
            expanded: observable,
            update: action,
            setSubmissionScope: action,
            setExpanded: action.bound,
            toggleExpanded: action.bound
        });

        this.refreshExpandedState();
    }

    refreshExpandedState() {
        this.sections.forEach(section => section.refreshExpandedState());
        this.setExpanded(this.sections.some(section => section.expanded));
    }

    setExpanded(expanded: boolean) {
        this.expanded = expanded;
    }

    toggleExpanded() {
        this.setExpanded(!this.expanded);
    }

    setSubmissionScope(submissionScope: SubmissionScope) {
        this.node.drafts.setSubmissionScope(
            { nodeId: this.node.id, itemId: 'SubmissionScope', layerId: this.id },
            submissionScope
        );
        this.submissionScope = submissionScope;
    }

    getSectionByInputGuid(guid: string) {
        for (const section of this.sections) {
            if (section.inputs.some(input => input.guid === guid)) {
                return section;
            }
        }

        return null;
    }

    update(data: LayerWithSectionsDto, sources: SourceModelBase[]) {
        this.submissionScope = data.submissionScope;
        // Input updates emit sync events; attach the complete rebuilt collection first.
        this.sections = data.sections.map(
            section => this.sections.find(s => s.id === section.id) ?? new SectionModel(this.node, section, sources)
        );

        data.sections.forEach((section, index) => this.sections[index].update(section, sources));
    }

    get dto(): LayerWithSectionsDto {
        return {
            id: this.id,
            name: this.name,
            submissionScope: this.submissionScope,
            sections: this.sections.map(section => section.dto)
        };
    }
}
